import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Star, Loader2, User } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StarRating = ({ rating, onRatingChange, interactive = false, size = "md" }) => {
  const [hoverRating, setHoverRating] = useState(0);
  const sizeClass = size === "sm" ? "w-4 h-4" : size === "lg" ? "w-6 h-6" : "w-5 h-5";
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onRatingChange?.(star)}
          onMouseEnter={() => interactive && setHoverRating(star)}
          onMouseLeave={() => interactive && setHoverRating(0)}
          className={`${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}`}
        >
          <Star
            className={`${sizeClass} ${(hoverRating || rating) >= star ? 'fill-[#f2c200] text-[#f2c200]' : 'text-muted-foreground/30'}`}
          />
        </button>
      ))}
    </div>
  );
};

const ReviewCard = ({ review }) => {
  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "d MMMM yyyy", { locale: it });
    } catch {
      return dateStr;
    }
  };

  return (
    <Card className="border-0 shadow-soft" data-testid={`review-${review.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
              <User className="w-4 h-4 text-muted-foreground" />
            </div>
            <div>
              <p className="font-medium text-foreground">{review.nome_autore}</p>
              <p className="text-xs text-muted-foreground">{formatDate(review.data_creazione)}</p>
            </div>
          </div>
          <StarRating rating={review.valutazione} size="sm" />
        </div>
        <p className="text-muted-foreground text-sm">{review.commento}</p>
      </CardContent>
    </Card>
  );
};

const ReviewForm = ({ sagraId, onReviewAdded }) => {
  const [rating, setRating] = useState(0);
  const [nome, setNome] = useState("");
  const [commento, setCommento] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!rating) {
      toast.error("Seleziona una valutazione");
      return;
    }
    if (!nome.trim()) {
      toast.error("Inserisci il tuo nome");
      return;
    }
    if (!commento.trim()) {
      toast.error("Inserisci un commento");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/sagre/${sagraId}/reviews`, {
        nome_autore: nome,
        valutazione: rating,
        commento
      });
      toast.success("Recensione pubblicata!");
      setRating(0);
      setNome("");
      setCommento("");
      onReviewAdded?.(response.data);
    } catch (error) {
      toast.error("Errore durante la pubblicazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="review-form">
      <div>
        <Label className="mb-2 block">La tua valutazione</Label>
        <StarRating rating={rating} onRatingChange={setRating} interactive size="lg" />
      </div>
      
      <div>
        <Label htmlFor="review-nome">Il tuo nome</Label>
        <Input
          id="review-nome"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Mario Rossi"
          className="mt-1.5"
          data-testid="review-nome-input"
        />
      </div>
      
      <div>
        <Label htmlFor="review-commento">La tua recensione</Label>
        <Textarea
          id="review-commento"
          value={commento}
          onChange={(e) => setCommento(e.target.value)}
          placeholder="Racconta la tua esperienza..."
          rows={3}
          className="mt-1.5"
          data-testid="review-commento-input"
        />
      </div>
      
      <Button 
        type="submit" 
        disabled={loading}
        className="rounded-full gap-2"
        data-testid="review-submit-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />}
        Pubblica Recensione
      </Button>
    </form>
  );
};

const ReviewsSection = ({ sagraId, initialRating = 0, initialCount = 0 }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(initialRating);
  const [count, setCount] = useState(initialCount);

  useEffect(() => {
    fetchReviews();
  }, [sagraId]);

  const fetchReviews = async () => {
    try {
      const [reviewsRes, ratingRes] = await Promise.all([
        axios.get(`${API}/sagre/${sagraId}/reviews`),
        axios.get(`${API}/sagre/${sagraId}/rating`)
      ]);
      setReviews(reviewsRes.data);
      setRating(ratingRes.data.valutazione_media);
      setCount(ratingRes.data.numero_recensioni);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewAdded = (newReview) => {
    setReviews(prev => [newReview, ...prev]);
    setCount(prev => prev + 1);
    // Recalculate average
    const newAvg = (rating * (count) + newReview.valutazione) / (count + 1);
    setRating(Math.round(newAvg * 10) / 10);
  };

  return (
    <div data-testid="reviews-section">
      {/* Rating Summary */}
      <div className="flex items-center gap-4 mb-6 p-4 bg-muted/50 rounded-xl">
        <div className="text-center">
          <p className="text-3xl font-bold text-foreground">{rating.toFixed(1)}</p>
          <StarRating rating={Math.round(rating)} size="sm" />
        </div>
        <div className="border-l border-border pl-4">
          <p className="text-muted-foreground">
            {count} {count === 1 ? 'recensione' : 'recensioni'}
          </p>
        </div>
      </div>

      {/* Review Form */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-foreground mb-4">Scrivi una recensione</h3>
        <ReviewForm sagraId={sagraId} onReviewAdded={handleReviewAdded} />
      </div>

      {/* Reviews List */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Recensioni</h3>
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse bg-muted rounded-xl h-24" />
            ))}
          </div>
        ) : reviews.length > 0 ? (
          <div className="space-y-4">
            {reviews.map(review => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-8">
            Nessuna recensione ancora. Sii il primo a recensire questa sagra!
          </p>
        )}
      </div>
    </div>
  );
};

export { StarRating, ReviewCard, ReviewForm, ReviewsSection };
export default ReviewsSection;
